import { useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Paper,
  useTheme,
  alpha,
  Fade,
  Grow,
} from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledProgressContainer = styled(Paper)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  padding: 0,
  width: '100%',
}));

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 8,
  borderRadius: 4,
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  '& .MuiLinearProgress-bar': {
    borderRadius: 4,
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
    boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.4)}`,
  },
}));

const LogContainer = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.background.default, 0.8),
  borderRadius: theme.spacing(1),
  padding: theme.spacing(1.5),
  minHeight: 90,
  maxHeight: 180,
  overflowY: 'auto',
  border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
  '&::-webkit-scrollbar': {
    width: 6,
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: alpha(theme.palette.action.hover, 0.1),
    borderRadius: 3,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: alpha(theme.palette.primary.main, 0.3),
    borderRadius: 3,
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.5),
    },
  },
}));

const ProgressIndicator = ({ progress = 0, logs = [], maxLogs = 20 }) => {
  const theme = useTheme();
  const logsEndRef = useRef(null);

  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  // Clamp progress between 0 and 100
  const clampedProgress = Math.round(Math.max(0, Math.min(progress, 100)));
  // Get the last n logs
  const visibleLogs = logs.slice(-maxLogs);

  return (
    <Grow in timeout={500}>
      <StyledProgressContainer elevation={0}>
        <Box sx={{ mb: 2.5 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: 'text.primary',
              fontFamily: theme.typography.fontFamily,
            }}
          >
            Fortschritt: {clampedProgress}%
          </Typography>
          <StyledLinearProgress
            variant="determinate"
            value={clampedProgress}
            sx={{
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          />
        </Box>

        <LogContainer>
          {visibleLogs.length === 0 ? (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontStyle: 'italic',
                textAlign: 'center',
                py: 2,
              }}
            >
              Warte auf Logs...
            </Typography>
          ) : (
            visibleLogs.map((log, idx) => (
              <Fade
                key={`${idx}-${log}`}
                in
                timeout={300}
                style={{ transitionDelay: `${idx * 50}ms` }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    mb: 0.75,
                    whiteSpace: 'pre-wrap',
                    color: 'text.primary',
                    fontWeight: 400,
                    fontSize: '0.85rem',
                    lineHeight: 1.4,
                    fontFamily: 'monospace',
                  }}
                >
                  {log}
                </Typography>
              </Fade>
            ))
          )}
          <div ref={logsEndRef} />
        </LogContainer>
      </StyledProgressContainer>
    </Grow>
  );
};

export default ProgressIndicator;
